import { FALLBACK_LANGUAGE } from '@frontends/util-i18n';
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import i18n from '../i18n';
import { EAuthState } from '../types';
import { TEntityConfig, useEntityConfig } from './useEntityConfig';

const axiosClient: AxiosInstance = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const $axios: AxiosInstance = axiosClient;

let localScopedAccessToken: string | null = null;
let refreshPromiseAccessToken: Promise<string | null> | null = null;

const getJwtUserId = (token: string): string | null => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));

    return payload.user_id ?? null;
  } catch {
    return null;
  }
};

interface IRetryableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface GenericError {
  code: number | string;
  context: unknown;
  detail: string;
  stack?: string;
}

const transformAxiosErrorToGenericError = (axiosError: AxiosError): GenericError => {
  const { message, response, status: statusCode, stack: axiosStackError } = axiosError;
  const data = response?.data ?? {};
  const { detail, code, context, stack } = data;

  return {
    detail: detail || message,
    code: code || statusCode,
    context: context || {},
    stack: stack || axiosStackError,
  };
};

const getFreshAccessToken = async (cfg: TEntityConfig): Promise<string | null> => {
  if (!refreshPromiseAccessToken) {
    refreshPromiseAccessToken = (async () => {
      try {
        const response = await $axios.put<{ access_token: string | null }>(
          `${cfg.VITE_URL_ID_BACKEND}/api/v1/auth/refresh-token`,
        );
        refreshPromiseAccessToken = null;

        return response.data.access_token ?? null;
      } catch (error) {
        refreshPromiseAccessToken = null;
        console.error('interceptor refreshing token', error);

        return null;
      }
    })();
  }

  return refreshPromiseAccessToken;
};

export const useAxiosInterceptors = (
  token: string | null,
  setToken: (token: string | null) => void,
  setAuthState: (state: EAuthState) => void,
): void => {
  const cfg = useEntityConfig();

  localScopedAccessToken = token ?? null;

  if (localScopedAccessToken) {
    $axios.defaults.headers.common['Authorization'] = `Bearer ${localScopedAccessToken}`;
  } else {
    delete $axios.defaults.headers.common['Authorization'];
  }

  useEffect(() => {
    const reqId = $axios.interceptors.request.use((req: InternalAxiosRequestConfig) => {
      req.headers = req.headers ?? {};
      req.headers['X-Trace-ID'] = uuidv4();
      req.headers['Accept-Language'] = i18n.language || FALLBACK_LANGUAGE;

      if (localScopedAccessToken) {
        req.headers['Authorization'] = `Bearer ${localScopedAccessToken}`;
      }

      return req;
    });

    const resId = $axios.interceptors.response.use(
      (res) => res,
      async (error: AxiosError) => {
        const original = error.config as IRetryableConfig | undefined;
        const status = error.response?.status;

        if (!original) {
          return Promise.reject(transformAxiosErrorToGenericError(error));
        }

        original.headers = original.headers ?? {};

        const isAuthUrl = original.url?.includes('/auth/refresh-token');
        const shouldTryRefresh = (status === 401 || status === 498) && !isAuthUrl;

        if (!shouldTryRefresh || original._retry) {
          return Promise.reject(transformAxiosErrorToGenericError(error));
        }

        original._retry = true;

        try {
          const freshAccessToken = await getFreshAccessToken(cfg);

          if (freshAccessToken) {
            const prevUserId = localScopedAccessToken ? getJwtUserId(localScopedAccessToken) : null;
            const newUserId = getJwtUserId(freshAccessToken);

            if (prevUserId && newUserId && prevUserId !== newUserId) {
              window.location.reload();

              // eslint-disable-next-line @typescript-eslint/no-empty-function
              return new Promise(() => {});
            }

            localScopedAccessToken = freshAccessToken;
            setToken(freshAccessToken);
            setAuthState(EAuthState.AUTHENTICATED);
            $axios.defaults.headers.common['Authorization'] = `Bearer ${freshAccessToken}`;
            original.headers['Authorization'] = `Bearer ${freshAccessToken}`;
            original.headers['X-Trace-ID'] = uuidv4();

            return $axios(original);
          }
        } catch {}

        localScopedAccessToken = null;
        setToken(null);
        setAuthState(EAuthState.UNAUTHENTICATED);
        delete $axios.defaults.headers.common['Authorization'];

        return Promise.reject(transformAxiosErrorToGenericError(error));
      },
    );

    return () => {
      $axios.interceptors.request.eject(reqId);
      $axios.interceptors.response.eject(resId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
